import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { RusswoodCanvas } from './RusswoodCanvas';
import { downloadModel, getDebugToken, setDebugToken } from 'store/actions/rpcActions';
import { isClientAuthorized, setClientAuthorized, addMessage } from 'store/actions/appActions';
import {
  clearUpdateData,
  clearSerializedData,
  setGuidURL,
  setReinitializeCanvas,
  setExportModel,
  setExportedModelData,
} from 'store/actions/canvasActions';
import { CanvasContainer } from './styles';
import { ActionArea } from './ActionArea';
import constants from 'constants/index';
import _ from 'lodash';

const { MESSAGE_TYPES } = constants;
const { NODE_ENV } = process.env;
const debug = NODE_ENV !== 'production';
let authCheckComplete;

const Russwood = () => {
  const { pathname, search } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canvasStore = useSelector(state => state.canvas);
  const rpcStore = useSelector(state => state.rpc);
  const appStore = useSelector(state => state.app);
  const debugToken = rpcStore.debugToken;
  const guidURL = useMemo(() => canvasStore.guidURL, [ canvasStore ]);
  const clientAuthorized = useMemo(() => appStore.clientAuthorized, [ appStore ]);
  const checkingAuthorized = useMemo(() => appStore.checkingAuthorized, [ appStore ]);
  const params = search.split('?').filter(param => param);
  const token = debug
    ? debugToken
    : params.find(param => param.includes('access_token'))?.split(`access_token=`)?.[1];

  const guid = pathname.split('russwood/')[1];

  const cleanup = useCallback(() => {
    dispatch(clearUpdateData());
    dispatch(clearSerializedData());
    dispatch(setGuidURL(null));
    dispatch(setReinitializeCanvas(false));
    dispatch(setExportModel(false));
    dispatch(setExportedModelData(null));
    dispatch(setClientAuthorized(false));
    dispatch(setDebugToken(null));
  }, [ dispatch ]);

  const getDebugAuthorization = useCallback(() => {
    if (debug && !debugToken) {
      const onSuccess = access_token => {
        navigate(`${pathname}?access_token=${access_token}`, { replace: true });
      };

      dispatch(getDebugToken({ onSuccess }));
    }
  }, [ debugToken, pathname, navigate, dispatch ]);

  const getDownloadURL = useCallback(() => {
    if (guid && clientAuthorized && _.isEmpty(guidURL)) {
      const args = { token, guid, format: 'GLB' };
      dispatch(downloadModel(args));
      return;
    }
  }, [ guid, dispatch, clientAuthorized, token, guidURL ]);

  const getClientAuthorized = useCallback(() => {
    if (!token) {
      const errorMessage = {
        message: `${debug ? 'Getting dev' : 'Missing'} access token...`,
        config: {
          type: debug ? MESSAGE_TYPES.info : MESSAGE_TYPES.error,
          timer: 3,
        },
      };

      dispatch(addMessage([ errorMessage ]));
    }

    if (token && !clientAuthorized && !checkingAuthorized && !authCheckComplete) {
      const args = {
        token,
        callback: () => authCheckComplete = true,
      };

      dispatch(isClientAuthorized(args));
      return;
    }
  }, [ checkingAuthorized, clientAuthorized, token, dispatch ]);

  useEffect(() => {
    return () => cleanup();
  }, [ cleanup ]);

  useEffect(() => {
    getDebugAuthorization();
  }, [ getDebugAuthorization ]);

  useEffect(() => {
      getClientAuthorized();
      getDownloadURL();
  }, [ debugToken, token, getDownloadURL, getClientAuthorized ]);

  return (
    <CanvasContainer>
      <RusswoodCanvas previewURL={guidURL}/>
      <ActionArea guidURL={guidURL}/>
    </CanvasContainer>
  );
};

export { Russwood };